import React, { FC, useEffect, useState } from 'react';
import { Card, CardMedia, Typography, CardActions, CircularProgress, Box, IconButton } from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ProjectMedia } from 'src/api/Api';
import clientService from 'src/clientService';
import EditCaptionDialog from './EditCaptionDialog';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { useAppState } from 'src/state-management/state-provider';

interface MediaCardProps {
  mediaItem: ProjectMedia;
  showActions?: boolean;
  onMediaClick?: () => void;
}

const MediaCard: FC<MediaCardProps> = ({ mediaItem, showActions = true, onMediaClick }) => {
  const { dispatch } = useAppState();
  const { showSnackbar, snackbarType } = useSnackbar();
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchDownloadUrl = async () => {
      setLoading(true);
      try {
        const response = await clientService.api.getProjectMediaDownloadUrlApiV1AdminProjectMediaMediaIdDownloadUrlGet(mediaItem.media_id);
        setDownloadUrl(response.data.download_url);
      } catch (err) {
        console.error('Failed to fetch download URL', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDownloadUrl();
  }, [mediaItem.media_id]);

  const handleOpenDialog = () => {
    if ((mediaItem.media_type === 'IMAGE' || mediaItem.media_type === 'VIDEO' || mediaItem.media_type === 'AUDIO') && onMediaClick) {
      onMediaClick();
    }
  };

  const handleDelete = async () => {
    try {
      await clientService.api.deleteProjectMediaApiV1AdminProjectMediaMediaIdDelete(mediaItem.media_id);
      dispatch({
        type: 'ADMIN_DELETE_PROJECT_MEDIA',
        payload: { projectId: mediaItem.project_id, mediaId: mediaItem.media_id }
      });
      showSnackbar({ message: 'Media deleted successfully', type: snackbarType.SUCCESS });
    } catch (err) {
      console.error('Delete failed', err);
      showSnackbar({ message: 'Failed to delete media', type: snackbarType.ERROR });
    }
  };

  const handleSaveCaption = async (newCaption: string) => {
    try {
      const response = await clientService.api.updateProjectMediaCaptionApiV1AdminProjectMediaMediaIdPatch(mediaItem.media_id, { caption: newCaption });
      dispatch({
        type: 'ADMIN_UPDATE_PROJECT_MEDIA',
        payload: { projectId: mediaItem.project_id, mediaItem: response.data }
      });
      showSnackbar({ message: 'Caption updated successfully', type: snackbarType.SUCCESS });
    } catch (err) {
      console.error('Update caption failed', err);
      showSnackbar({ message: 'Failed to update caption', type: snackbarType.ERROR });
    }
  };

  const renderMedia = () => {
    if (loading) {
      return (
        <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!downloadUrl) {
      return <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Preview not available</Typography>
      </Box>;
    }

    const isViewable = mediaItem.media_type === 'IMAGE' || mediaItem.media_type === 'VIDEO' || mediaItem.media_type === 'AUDIO';

    const mediaContent = () => {
      if (mediaItem.media_type === 'IMAGE') {
        return <CardMedia component="img" sx={{ height: '100%', objectFit: 'cover' }} image={downloadUrl} alt={mediaItem.caption || 'Project media'} />;
      } else if (mediaItem.media_type === 'VIDEO') {
        return <CardMedia component="video" sx={{ height: '100%', objectFit: 'cover' }} src={downloadUrl} />;
      } else if (mediaItem.media_type === 'AUDIO') {
        return (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#1A1A1A', p: 1 }}>
            <MusicNoteIcon sx={{ fontSize: 40, mb: 1, color: '#10B981', opacity: 0.8 }} />
            <audio 
              src={downloadUrl} 
              controls 
              style={{ width: '90%', height: '32px' }} 
              onClick={(e) => e.stopPropagation()} 
            />
          </Box>
        );
      }
      return null;
    };

    return (
      <Box sx={{ position: 'relative', height: 160, cursor: isViewable ? 'pointer' : 'default' }} onClick={handleOpenDialog}>
        {mediaContent()}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.9) 100%)',
          color: 'white',
          p: 1.5,
          pointerEvents: 'none',
        }}>
          <Typography variant="caption" sx={{ alignSelf: 'flex-end', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
            {new Date(mediaItem.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }} noWrap title={mediaItem.caption || ''}>
            {mediaItem.caption || ''}
          </Typography>
        </Box>
        {isViewable && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.3s',
            '&:hover': {
              opacity: 1,
              bgcolor: 'rgba(0, 0, 0, 0.4)',
            },
          }}>
            <VisibilityIcon fontSize="large" />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Card sx={{ 
        backgroundColor: '#111', 
        border: '1px solid #222', 
        borderRadius: '16px', 
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: '#10B981',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }
      }}>
        {renderMedia()}
        {showActions && (
          <CardActions sx={{ 
            justifyContent: 'flex-end', 
            bgcolor: '#151515', 
            p: 1, 
            borderTop: '1px solid #222',
            gap: 1
          }}>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); setEditDialogOpen(true); }}
              sx={{ color: '#999', '&:hover': { color: '#10B981', bgcolor: 'rgba(16, 185, 129, 0.1)' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              sx={{ color: '#999', '&:hover': { color: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </CardActions>
        )}
      </Card>
      <EditCaptionDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        mediaItem={mediaItem}
        onSave={handleSaveCaption}
      />
    </>
  );
};

export default MediaCard;
